/**
 * ProposalWhy — the expandable "Why?" section on an approval-inbox row.
 *
 * Renders the proposer-supplied explanation carried on an ApprovalItem:
 * a one-sentence `rationale` plus concrete `evidence` bullets (e.g.
 * "Follow-up 21 days overdue"). Both fields are optional everywhere —
 * when neither is present this renders NOTHING, so older proposals keep
 * working with no migration and no empty chrome.
 *
 * Collapsed by default behind a small "Why?" toggle so the inbox stays
 * scannable; expanding is a local UI state, never a write.
 */

import { useState } from 'react';
import { C } from '../lib/tokens';

export interface ProposalWhyProps {
  rationale?: string;
  evidence?: string[];
}

export function ProposalWhy({ rationale, evidence }: ProposalWhyProps) {
  const [open, setOpen] = useState(false);
  const bullets = (evidence ?? []).filter((e) => typeof e === 'string' && e.trim());
  const hasRationale = typeof rationale === 'string' && rationale.trim().length > 0;
  // Missing fields render nothing — older proposals keep working.
  if (!hasRationale && bullets.length === 0) return null;

  return (
    <span style={{ display: 'block' }}>
      <button
        className="glass-btn"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        aria-expanded={open}
        title="Why did the agent propose this?"
        style={{ marginTop: 4, padding: '2px 6px', fontSize: 9 }}
      >
        Why? {open ? '▾' : '▸'}
      </button>
      {open && (
        <span className="t-meta" style={{ display: 'block', marginTop: 4, fontSize: 11 }}>
          {hasRationale && (
            <span style={{ display: 'block', color: C.textMuted }}>{rationale}</span>
          )}
          {bullets.map((line, i) => (
            // Rendered as bullet lines (not <ul>) so the markup stays valid
            // inside the inbox row's inline containers.
            <span key={i} style={{ display: 'block', marginTop: 2 }}>
              • {line}
            </span>
          ))}
        </span>
      )}
    </span>
  );
}
